import { Namespace, useT } from "@racoon/i18n";
import { useTruckStatus } from "./use-truck-status.hook";
import { IcCar } from "@racoon/icons";

interface TruckProgressProps {
  serviceProviderId?: string | number | bigint;
  districtOrganizationIds?: (string | number | bigint)[];
}

export const TruckProgress = ({
  serviceProviderId,
  districtOrganizationIds = [],
}: TruckProgressProps) => {
  const t = useT();

  const truckStatus = useTruckStatus({
    districtOrganizationIds,
    serviceProviderIds: serviceProviderId != null ? [serviceProviderId] : [],
  });

  return (
    <div className="space-y-4 py-4">
      {/* Progress bar */}
      <div className="px-4">
        <div className="flex gap-2 items-start w-full">
          <div className="overflow-hidden shrink-0 w-6 h-6">
            <IcCar className="w-6 h-6" />
          </div>
          <div className="flex flex-1 h-6 rounded-lg overflow-hidden">
            {/* On line */}
            <div
              className="bg-[#34c759] flex items-center justify-center py-1 transition-[flex] duration-500"
              style={{ flex: truckStatus.truckOnLine }}
            >
              {truckStatus.truckOnLine > 0 && (
                <p className="font-medium text-xs leading-4 tracking-tight text-white whitespace-nowrap px-1">
                  {Math.round(truckStatus.truckOnLine)}
                </p>
              )}
            </div>
            {/* Idle */}
            <div
              className="bg-text-secondary flex items-center justify-center py-1 transition-[flex] duration-500"
              style={{ flex: truckStatus.truckIdle }}
            >
              {truckStatus.truckIdle > 0 && (
                <p className="font-medium text-xs leading-4 tracking-tight text-white whitespace-nowrap px-1">
                  {Math.round(truckStatus.truckIdle)}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Details grid */}
      <div className="grid gap-y-2 gap-x-12 px-4" style={{ gridTemplateColumns: "1fr auto auto" }}>
        {/* On line */}
        <div className="flex gap-1 items-center">
          <div className="w-3 h-3 rounded-full bg-[#34c759] shrink-0" />
          <p className="font-medium text-sm leading-[14px] tracking-tight text-text-primary">
            {t(Namespace.Govdash, "status.onLine")}
          </p>
        </div>
        <p className="font-medium text-sm leading-[14px] tracking-tight text-text-primary text-right">
          {t(Namespace.Govdash, "status.trucks_count", { count: truckStatus.truckOnLine })}
        </p>
        <p className="font-normal text-sm leading-[14px] tracking-tight text-text-primary text-right">
          {truckStatus.truckOnLinePercent}%
        </p>

        {/* Idle */}
        <div className="flex gap-1 items-center">
          <div className="w-3 h-3 rounded-full bg-text-secondary shrink-0" />
          <p className="font-medium text-sm leading-[14px] tracking-tight text-text-primary">
            {t(Namespace.Govdash, "status.idle")}
          </p>
        </div>
        <p className="font-medium text-sm leading-[14px] tracking-tight text-text-primary text-right">
          {t(Namespace.Govdash, "status.trucks_count", { count: truckStatus.truckIdle })}
        </p>
        <p className="font-normal text-sm leading-[14px] tracking-tight text-text-primary text-right">
          {truckStatus.truckIdlePercent}%
        </p>
      </div>
    </div>
  );
};
